import React, { useMemo, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, FormHelperText, TextField, ListSubheader } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { ContainerCategory } from '../../../core/interfaces/category.interface';

interface ContainerCategorySelectProps {
  value: number | undefined | string;
  onChange: (value: number | undefined | string) => void;
  categories: ContainerCategory[];
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  showAllOption?: boolean;
  allOptionLabel?: string;
  size?: 'small' | 'medium';
}

interface CategoryTreeItem {
  id: number;
  name: string;
  description: string;
  acronym?: string | null;
  container_type_id: number;
  parent_id?: number | null;
  child_categories?: ContainerCategory[];
  children?: CategoryTreeItem[];
}

const ContainerCategorySelect: React.FC<ContainerCategorySelectProps> = ({
  value,
  onChange,
  categories,
  label = 'Categoría',
  required = false,
  error = false,
  helperText,
  disabled = false,
  showAllOption = false,
  allOptionLabel = 'Todos',
  size = 'medium',
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Función para verificar si una categoría o sus hijos coinciden con el término de búsqueda
  const matchesSearch = (item: CategoryTreeItem, search: string): boolean => {
    if (!search || search.trim() === '') return true;
    
    const searchLower = search.toLowerCase().trim();
    const nameMatch = item.name.toLowerCase().includes(searchLower);
    const descriptionMatch = item.description?.toLowerCase().includes(searchLower);
    const acronymMatch = item.acronym?.toLowerCase().includes(searchLower);
    const idMatch = String(item.id).toLowerCase().includes(searchLower);
    
    // Si la categoría coincide, retornar true
    if (nameMatch || descriptionMatch || acronymMatch || idMatch) return true;
    
    // Si tiene hijos, verificar si alguno coincide
    if (item.children && item.children.length > 0) {
      return item.children.some(child => matchesSearch(child, search));
    }
    
    return false;
  };

  // Función recursiva para filtrar el árbol manteniendo la jerarquía
  const filterTree = (items: CategoryTreeItem[], search: string): CategoryTreeItem[] => {
    if (!search || search.trim() === '') {
      return items;
    }

    const filtered: CategoryTreeItem[] = [];

    items.forEach(item => {
      const itemMatches = matchesSearch(item, search);
      const filteredChildren = item.children && item.children.length > 0
        ? filterTree(item.children, search)
        : [];

      // Si la categoría coincide o tiene hijos que coinciden, incluirla
      if (itemMatches || filteredChildren.length > 0) {
        filtered.push({
          ...item,
          children: filteredChildren.length > 0 ? filteredChildren : undefined
        });
      }
    });

    return filtered;
  };
  
  // Construir el árbol de categorías
  const categoryTree = useMemo(() => {
    const tree: CategoryTreeItem[] = [];
    const categoryMap = new Map<number, CategoryTreeItem>();

    // Función recursiva para convertir ContainerCategory a CategoryTreeItem procesando todos los niveles
    const convertToTreeItem = (category: ContainerCategory): CategoryTreeItem => {
      const children: CategoryTreeItem[] = [];
      
      // Si tiene child_categories, procesarlos recursivamente
      if (category.child_categories && category.child_categories.length > 0) {
        category.child_categories.forEach(child => {
          children.push(convertToTreeItem(child));
        });
      }

      return {
        ...category,
        acronym: category.acronym || null,
        children: children
      };
    };

    // Primero, crear todos los nodos procesando recursivamente los child_categories
    categories.forEach((category) => {
      const treeItem = convertToTreeItem(category);
      categoryMap.set(category.id, treeItem);
    });

    // Si las categorías vienen con child_categories estructuradas, solo agregar las raíces al árbol
    // Si vienen planas con parent_id, construir el árbol desde parent_id
    const hasStructuredChildren = categories.some(cat => cat.child_categories && cat.child_categories.length > 0);
    
    if (hasStructuredChildren) {
      // Las categorías vienen estructuradas, solo agregar las raíces (sin parent_id)
      categories.forEach((category) => {
        if (!category.parent_id || category.parent_id === null) {
          const node = categoryMap.get(category.id)!;
          if (!tree.find(t => t.id === node.id)) {
            tree.push(node);
          }
        }
      });
    } else {
      // Las categorías vienen planas, construir el árbol desde parent_id
      categories.forEach((category) => {
        const node = categoryMap.get(category.id)!;
        
        if (!category.parent_id || category.parent_id === null) {
          // Es una raíz
          if (!tree.find(t => t.id === node.id)) {
            tree.push(node);
          }
        } else {
          // Es una categoría hijo - agregar al padre
          const parent = categoryMap.get(category.parent_id);
          if (parent) {
            if (!parent.children) {
              parent.children = [];
            }
            // Evitar duplicados
            if (!parent.children.find(c => c.id === node.id)) {
              parent.children.push(node);
            }
          } else {
            // Si el padre no está en la lista, agregar como raíz
            if (!tree.find(t => t.id === node.id)) {
              tree.push(node);
            }
          }
        }
      });
    }

    // Crear una copia mutable del árbol antes de ordenar
    const createMutableTree = (items: CategoryTreeItem[]): CategoryTreeItem[] => {
      return items.map(item => ({
        ...item,
        children: item.children && item.children.length > 0 
          ? createMutableTree(item.children) 
          : []
      }));
    };

    const mutableTree = createMutableTree(tree);

    // Ordenar por nombre (ahora todos los arrays son mutables)
    const sortCategories = (items: CategoryTreeItem[]): void => {
      items.sort((a, b) => a.name.localeCompare(b.name));
      items.forEach((item) => {
        if (item.children && item.children.length > 0) {
          sortCategories(item.children);
        }
      });
    };

    sortCategories(mutableTree);
    return mutableTree;
  }, [categories]);

  // Filtrar el árbol según el término de búsqueda
  const filteredTree = useMemo(() => {
    return filterTree(categoryTree, searchTerm);
  }, [categoryTree, searchTerm]);

  // Función recursiva para encontrar una categoría en el árbol
  const findCategoryInTree = (tree: CategoryTreeItem[], categoryId: number): CategoryTreeItem | null => {
    for (const item of tree) {
      if (item.id === categoryId) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = findCategoryInTree(item.children, categoryId);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  // Renderizar categorías recursivamente
  const renderCategoryItems = (items: CategoryTreeItem[], level: number = 0): React.ReactNode[] => {
    const result: React.ReactNode[] = [];

    items.forEach((category) => {
      const indent = level * 24; // 24px de indentación por nivel
      const prefix = level > 0 ? '└─ ' : '';
      const label = `${category.acronym ? `${category.acronym} - ` : ''}${category.name}`;

      result.push(
        <MenuItem key={category.id} value={category.id} sx={{ pl: `${indent + 16}px` }}>
          <span style={{ fontWeight: level === 0 ? 600 : 400 }}>
            {prefix}
            {label}
          </span>
        </MenuItem>
      );

      // Renderizar hijos si existen
      if (category.children && category.children.length > 0) {
        result.push(...renderCategoryItems(category.children, level + 1));
      }
    });

    return result;
  };

  const handleChange = (e: any) => {
    const newValue = e.target.value;
    if (newValue === '' || newValue === null) {
      onChange(undefined);
    } else if (newValue === 'all') {
      onChange('all');
    } else {
      onChange(Number(newValue));
    }
  };

  return (
    <FormControl variant="outlined" fullWidth required={required} error={error} disabled={disabled} size={size}>
      <InputLabel id="container-category-select-label">{label}</InputLabel>
      <Select
        labelId="container-category-select-label"
        label={label}
        value={value || ''}
        onChange={handleChange}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
        }}
      >
        <ListSubheader>
          <TextField
            fullWidth
            size="small"
            placeholder="Buscar categoría..."
            value={searchTerm}
            onChange={(e) => {
              e.stopPropagation();
              setSearchTerm(e.target.value);
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />,
            }}
            sx={{ mt: 1, mb: 1 }}
          />
        </ListSubheader>
        {showAllOption && (
          <MenuItem value="all">
            <strong>{allOptionLabel}</strong>
          </MenuItem>
        )}
        {filteredTree.length === 0 ? (
          <MenuItem disabled value="">
            <em>{searchTerm ? 'No se encontraron categorías' : 'No hay categorías disponibles'}</em>
          </MenuItem>
        ) : (
          renderCategoryItems(filteredTree)
        )}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default ContainerCategorySelect;

